import type { FormState } from 'informed';
import type {
  ColourInput,
  CustomButtonInput,
  PaletteInput,
  TypographyInput,
  UpdateWhiteLabelSettingsMutationVariables,
  WhiteLabelColourType,
  WhiteLabelSettingsRowForManagementFragment,
} from '../../../../../gql/graphql';
import {
  isValidGoogleTagId,
  normalizeGoogleTagId,
} from '../../../../../util/googleTags';

/** Custom button as returned by the WL row (title, url, order). */
export type SettingsCustomButton =
  NonNullable<WhiteLabelSettingsRowForManagementFragment>['customButtons'][number];

/** Informed row shape — `CustomButtonInput` fields used in the form (order added on save). */
export type WhiteLabelCustomButtonFormRow = Pick<
  CustomButtonInput,
  'title' | 'url'
>;

/**
 * Query → form: stable sort by `order`, then rows for Informed.
 */
export function toWhiteLabelCustomButtonFormRows(
  buttons: readonly SettingsCustomButton[],
): WhiteLabelCustomButtonFormRow[] {
  return [...buttons]
    .sort((a, b) => {
      const aRank = Number.isFinite(a.order) ? a.order : 0;
      const bRank = Number.isFinite(b.order) ? b.order : 0;
      return aRank - bRank;
    })
    .map((btn) => ({
      title: btn.title ?? '',
      url: btn.url ?? '',
    }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Plain object from `JSON.parse` — not arrays, not null. */
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Deep clone JSON-safe Informed reset baseline — `structuredClone` is not in this project's TS lib. */
export function cloneFormResetBaseline(
  value: Record<string, unknown>,
): Record<string, unknown> {
  const parsed: unknown = JSON.parse(JSON.stringify(value));
  if (isJsonObject(parsed)) {
    return parsed;
  }
  return {};
}

/** Coerce an Informed / unknown field value to string (empty when absent). */
export function formFieldString(value: unknown): string {
  if (value == null) return '';
  return typeof value === 'string' ? value : String(value);
}

/**
 * Map an unknown array (e.g. Informed ArrayField values) to typed rows plus `order` for mutations.
 */
export function mapUnknownArrayWithOrder<TRow extends object>(
  rows: unknown,
  parseRow: (rec: Record<string, unknown>) => TRow | null,
): Array<TRow & { order: number }> | undefined {
  if (!Array.isArray(rows) || rows.length === 0) return undefined;
  // `order` is the index in the source array (including skipped entries), not a dense rank.
  const result = rows.reduce<Array<TRow & { order: number }>>(
    (acc, el, index) => {
      if (!isRecord(el)) return acc;
      const parsed = parseRow(el);
      if (parsed === null) return acc;
      acc.push({ ...parsed, order: index });
      return acc;
    },
    [],
  );
  return result.length > 0 ? result : undefined;
}

/** Palette fragment as selected on the WL row. */
type SettingsPalette = NonNullable<
  WhiteLabelSettingsRowForManagementFragment['palette']
>;

/** Typography fragment as selected on the WL row. */
type SettingsTypography = NonNullable<
  WhiteLabelSettingsRowForManagementFragment['typography']
>;

/** Heading keys on `TypographyInput` (shape from schema; levels list still comes from the API). */
type TypographyHeadingInputKey = keyof Pick<
  TypographyInput,
  'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
>;

/**
 * Type guard: role is listed by the backend (`whiteLabelPaletteColourRoles`) and safe to use as `PaletteInput` key.
 * Runtime list is the source of truth; predicate matches mutation input shape.
 */
export function isPaletteInputKey(
  role: string,
  allowedPaletteRoles: readonly string[],
): role is keyof PaletteInput {
  return allowedPaletteRoles.includes(role);
}

/** Swatch keys on `WhiteLabelColourType` / palette form (`ColourInput` also has separate `contrastText`). */
export type WhiteLabelSwatchShadeKey = keyof Pick<
  WhiteLabelColourType,
  'lighter' | 'light' | 'main' | 'dark' | 'darker'
>;

const NAMED_COLOUR_NORMALIZATIONS: Readonly<Record<string, string>> = {
  black: '#000000',
  white: '#ffffff',
};

const MUI_SUPPORTED_COLOUR_PATTERN =
  /^(#[0-9a-f]{3}|#[0-9a-f]{6}|rgba?\(.+\)|hsla?\(.+\)|color\(.+\))$/i;

export function normalizeWhiteLabelColourForMui(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const namedColour = NAMED_COLOUR_NORMALIZATIONS[trimmed.toLowerCase()];
  if (namedColour != null) return namedColour;

  return MUI_SUPPORTED_COLOUR_PATTERN.test(trimmed) ? trimmed : null;
}

export function validateWhiteLabelColourField(
  value: unknown,
): string | undefined {
  const raw = formFieldString(value).trim();
  if (!raw) return undefined;
  return normalizeWhiteLabelColourForMui(raw) == null
    ? 'Use #hex, rgb(), rgba(), hsl(), hsla(), or color(). Named colours are not supported.'
    : undefined;
}

export function validateGoogleAnalyticsToken(
  value: unknown,
): string | undefined {
  const raw = formFieldString(value);
  return isValidGoogleTagId(raw)
    ? undefined
    : 'Use a Google Tag Manager ID (GTM-...) or GA4 measurement ID (G-...).';
}

function requiredValidColourOrNull(
  value: unknown,
  fieldName: string,
): string | null {
  const raw = formFieldString(value);
  if (!raw.trim()) return null;
  const normalized = normalizeWhiteLabelColourForMui(raw);
  if (normalized == null) {
    throw new Error(`Invalid white-label colour for ${fieldName}: ${raw}`);
  }
  return normalized;
}

/**
 * Type guard: shade is listed by the backend (`whiteLabelColourShadeFields`) and maps to a colour swatch field.
 */
export function isColourShadeField(
  shade: string,
  allowedShadeFields: readonly string[],
): shade is WhiteLabelSwatchShadeKey {
  if (!allowedShadeFields.includes(shade)) return false;
  return (
    shade === 'lighter' ||
    shade === 'light' ||
    shade === 'main' ||
    shade === 'dark' ||
    shade === 'darker'
  );
}

/**
 * Type guard: level is listed by the backend (`whiteLabelTypographyHeadingLevels`) and maps to heading inputs.
 */
export function isTypographyInputHeadingKey(
  level: string,
  allowedHeadingLevels: readonly string[],
): level is TypographyHeadingInputKey {
  return allowedHeadingLevels.includes(level);
}

/**
 * Resolve palette swatch for a role name.
 * Unknown roles yield an empty object.
 */
export function colourSwatchForRole(
  palette: SettingsPalette | null | undefined,
  role: string,
  allowedPaletteRoles: readonly string[],
): Partial<WhiteLabelColourType> {
  if (!palette || !isPaletteInputKey(role, allowedPaletteRoles)) return {};
  const swatch = palette[role];
  return swatch ?? {};
}

/**
 * Read one shade string from a swatch.
 * Unknown shades yield an empty string.
 */
export function shadeStringFromSwatch(
  swatch: Partial<WhiteLabelColourType>,
  shade: string,
  allowedShadeFields: readonly string[],
): string {
  if (!isColourShadeField(shade, allowedShadeFields)) return '';
  const val = swatch[shade];
  return typeof val === 'string' ? val : '';
}

/**
 * Heading font name for a level from typography metadata.
 */
export function headingFontNameForLevel(
  typography: SettingsTypography | null | undefined,
  level: string,
  allowedHeadingLevels: readonly string[],
): string {
  if (!typography || !isTypographyInputHeadingKey(level, allowedHeadingLevels))
    return '';
  switch (level) {
    case 'h1':
      return typography.h1?.name ?? '';
    case 'h2':
      return typography.h2?.name ?? '';
    case 'h3':
      return typography.h3?.name ?? '';
    case 'h4':
      return typography.h4?.name ?? '';
    case 'h5':
      return typography.h5?.name ?? '';
    case 'h6':
      return typography.h6?.name ?? '';
    default:
      // API list should match schema heading keys; keep safe if metadata drifts
      return '';
  }
}

/** Merge one shade value into `ColourInput`. Unknown shades are ignored. */
export function applyShadeToColourInput(
  colour: ColourInput,
  shade: string,
  value: string,
  allowedShadeFields: readonly string[],
): void {
  if (!isColourShadeField(shade, allowedShadeFields)) return;
  colour[shade] = value;
}

/** Non-empty strings from GraphQL `[String]` list fields (drops null entries and blanks). */
export function normalizeGraphqlStringList(list: unknown): string[] {
  const out: string[] = [];
  if (!Array.isArray(list)) return out;
  for (const item of list) {
    if (typeof item === 'string' && item.length > 0) out.push(item);
  }
  return out;
}

// --- Company account form: query row → Informed values → mutation variables ---

/** WL row returned by the management settings query. */
export type WhiteLabelSettingsRow = WhiteLabelSettingsRowForManagementFragment;

/** Informed `values` at submit plus `originalSlug` (current DB slug) merged in the submit handler. */
export type WhiteLabelSettingsSubmitValues = FormState['values'] & {
  originalSlug: string;
};

/** Narrow `unknown` form values to optional mutation strings (empty → undefined). */
export function stringOrUndefined(value: unknown): string | undefined {
  if (value == null) return undefined;
  const s = typeof value === 'string' ? value : String(value);
  return s === '' ? undefined : s;
}

/** Preserve `false`; only null/undefined (or non-boolean noise) become undefined. */
export function booleanOrUndefined(value: unknown): boolean | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'boolean') return value;
  return undefined;
}

export function insuranceTypesOrUndefined(
  value: unknown,
): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const out: string[] = [];
  for (const t of value) {
    if (t != null && t !== '') out.push(String(t));
  }
  return out.length > 0 ? out : undefined;
}

/** Maps the GraphQL response to a flat Informed initialValues object */
export function buildInitialValues(
  raw: WhiteLabelSettingsRow | null | undefined,
  paletteColourRoles: readonly string[],
  colourShadeFields: readonly string[],
  typographyHeadingLevels: readonly string[],
) {
  if (!raw) return {};

  const palette = raw.palette;
  const typography = raw.typography;

  // Flatten palette: palette_primary_main, etc. — iterate metadata lists only (no duplicate const arrays).
  const paletteValues: Record<string, string> = {};
  for (const role of paletteColourRoles) {
    const swatch = colourSwatchForRole(palette, role, paletteColourRoles);
    for (const shade of colourShadeFields) {
      paletteValues[`palette_${role}_${shade}`] = shadeStringFromSwatch(
        swatch,
        shade,
        colourShadeFields,
      );
    }
  }

  // MUI contrastText for brand colours — stored on WhiteLabelPalette (GraphQL camelCase).
  if (palette) {
    if (paletteColourRoles.includes('primary')) {
      const fromApi = palette.primaryContrastText;
      paletteValues.palette_primary_contrastText =
        typeof fromApi === 'string' ? fromApi : '';
    }
    if (paletteColourRoles.includes('secondary')) {
      const fromApi = palette.secondaryContrastText;
      paletteValues.palette_secondary_contrastText =
        typeof fromApi === 'string' ? fromApi : '';
    }
  }

  // Flatten typography: use font name (the valid FontName choice) not the CSS family string
  const typographyValues: Record<string, string | number | undefined> = {
    fontFamily: typography?.fontFamily?.name ?? '',
    subtitleFontWeight: typography?.subtitleFontWeight ?? '',
  };
  for (const level of typographyHeadingLevels) {
    typographyValues[`typography_${level}`] = headingFontNameForLevel(
      typography,
      level,
      typographyHeadingLevels,
    );
  }

  // Flatten types: string array
  const types = (raw.types ?? []).map((t) => t.type);

  const customButtons: WhiteLabelCustomButtonFormRow[] =
    toWhiteLabelCustomButtonFormRows(raw.customButtons);

  return {
    slug: raw.slug ?? '',
    host: raw.host ?? '',
    companyName: raw.companyName ?? '',
    phone: raw.phone ?? '',
    email: raw.email ?? '',
    hours: raw.hours ?? '',
    chatLink: raw.chatLink ?? '',
    privacyLink: raw.privacyLink ?? '',
    title: raw.title ?? '',
    thankYouText: raw.thankYouText ?? '',
    policystreamIntroductoryText: raw.policystreamIntroductoryText ?? '',
    homepage: raw.homepage ?? '',
    googleAnalyticsToken: raw.googleAnalyticsToken ?? '',
    includeCustomShadows: raw.includeCustomShadows ?? false,
    sendEmailOnContactInformationCompleted:
      raw.sendEmailOnContactInformationCompleted ?? false,
    contactInformationFirst: raw.contactInformationFirst ?? false,
    allowApplicationUpload: raw.allowApplicationUpload ?? false,
    aiAssistedBusinessSelection: raw.aiAssistedBusinessSelection ?? false,
    types,
    customButtons,
    ...paletteValues,
    ...typographyValues,
  };
}

/** Converts flat form values back to the nested mutation variables shape */
export function buildMutationVariables(
  values: WhiteLabelSettingsSubmitValues,
  token: string,
  isAdmin: boolean,
  paletteColourRoles: readonly string[],
  colourShadeFields: readonly string[],
  typographyHeadingLevels: readonly string[],
): UpdateWhiteLabelSettingsMutationVariables {
  const mainShade =
    colourShadeFields.find((s) => s === 'main') ?? colourShadeFields[0] ?? '';

  // Re-assemble palette from flat keys (`ColourInput.main` is required on the wire).
  const palette: PaletteInput = {};
  for (const role of paletteColourRoles) {
    if (!mainShade || !isPaletteInputKey(role, paletteColourRoles)) continue;
    const mainVal = values[`palette_${role}_${mainShade}`];
    const main = requiredValidColourOrNull(
      mainVal,
      `palette_${role}_${mainShade}`,
    );
    if (main == null) continue;
    const colour: ColourInput = { main };
    for (const shade of colourShadeFields) {
      if (shade === mainShade) continue;
      const v = values[`palette_${role}_${shade}`];
      const normalizedShade = requiredValidColourOrNull(
        v,
        `palette_${role}_${shade}`,
      );
      if (normalizedShade != null) {
        applyShadeToColourInput(
          colour,
          shade,
          normalizedShade,
          colourShadeFields,
        );
      }
    }
    if (role === 'primary' || role === 'secondary') {
      // Always send so the backend does not treat a missing field as "clear stored contrast".
      colour.contrastText =
        requiredValidColourOrNull(
          values[`palette_${role}_contrastText`],
          `palette_${role}_contrastText`,
        ) ?? '';
    }
    palette[role] = colour;
  }

  const typography: TypographyInput = {};
  if (values.fontFamily != null && values.fontFamily !== '')
    typography.fontFamily = String(values.fontFamily);
  if (values.subtitleFontWeight != null && values.subtitleFontWeight !== '')
    typography.subtitleFontWeight = Number(values.subtitleFontWeight);
  for (const level of typographyHeadingLevels) {
    if (!isTypographyInputHeadingKey(level, typographyHeadingLevels)) continue;
    const v = values[`typography_${level}`];
    if (v != null && v !== '') typography[level] = String(v);
  }

  // originalSlug: this org's row slug when the loaded settings belong to us; otherwise '' (borrowed default).
  // Mutation `slug` must be the lookup key — for first own row, that is the chosen slug from the form.
  const formSlugTrimmed = values.slug != null ? String(values.slug).trim() : '';
  const originalTrimmed = values.originalSlug.trim();
  const mutationSlug =
    originalTrimmed.length > 0 ? originalTrimmed : formSlugTrimmed;
  const newSlug =
    isAdmin && originalTrimmed.length > 0 && formSlugTrimmed !== originalTrimmed
      ? formSlugTrimmed
      : undefined;

  return {
    token,
    slug: mutationSlug,
    newSlug,
    host: isAdmin ? stringOrUndefined(values.host) : undefined,
    companyName: stringOrUndefined(values.companyName),
    phone: stringOrUndefined(values.phone),
    email: stringOrUndefined(values.email),
    hours: stringOrUndefined(values.hours),
    chatLink: stringOrUndefined(values.chatLink),
    privacyLink: stringOrUndefined(values.privacyLink),
    title: stringOrUndefined(values.title),
    thankYouText: stringOrUndefined(values.thankYouText),
    policystreamIntroductoryText: stringOrUndefined(
      values.policystreamIntroductoryText,
    ),
    homepage: stringOrUndefined(values.homepage),
    googleAnalyticsToken: (() => {
      const normalized = normalizeGoogleTagId(
        formFieldString(values.googleAnalyticsToken),
      );
      if (!isValidGoogleTagId(normalized)) {
        throw new Error(
          'Invalid Google tag ID. Use a Google Tag Manager ID (GTM-...) or GA4 measurement ID (G-...).',
        );
      }
      return normalized;
    })(),
    includeCustomShadows: booleanOrUndefined(values.includeCustomShadows),
    sendEmailOnContactInformationCompleted: booleanOrUndefined(
      values.sendEmailOnContactInformationCompleted,
    ),
    contactInformationFirst: booleanOrUndefined(values.contactInformationFirst),
    allowApplicationUpload: booleanOrUndefined(values.allowApplicationUpload),
    aiAssistedBusinessSelection: booleanOrUndefined(
      values.aiAssistedBusinessSelection,
    ),
    palette: Object.keys(palette).length > 0 ? palette : undefined,
    typography: Object.keys(typography).length > 0 ? typography : undefined,
    types: insuranceTypesOrUndefined(values.types),
    // Row order in the form (including drag-reorder) becomes persistent order in the DB.
    customButtons: mapUnknownArrayWithOrder(values.customButtons, (rec) => ({
      title: formFieldString(rec.title),
      url: formFieldString(rec.url),
    })),
  };
}
